'use client';

import { useState, useRef, useEffect, useId } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, Check } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  /** Optional decorative marker shown before the option label. */
  leading?: string;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Accessible name for custom listbox trigger when no visible label is present. */
  ariaLabel?: string;
  /** Stable id so a visible label can be associated with the trigger. */
  id?: string;
  /** Optional form field name. A hidden input mirrors the selected value. */
  name?: string;
  /** Optional hook for keyboard shortcuts or analytics that target the trigger. */
  dataAct?: string;
}

export function Select({
  value,
  defaultValue,
  onChange,
  options,
  placeholder = 'Select...',
  className,
  disabled = false,
  ariaLabel,
  id,
  name,
  dataAct,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
  const selectRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const generatedId = useId();
  const triggerId = id ?? generatedId;

  const selectedValue = value ?? uncontrolledValue;
  const selectedOption = options.find((opt) => opt.value === selectedValue);

  // Close dropdown when clicking outside
  // SAFETY: The surrounding typed contract establishes the asserted representation.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!isOpen) return;

      const currentIndex = options.findIndex((opt) => opt.value === selectedValue);
      let newIndex = currentIndex;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          newIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
          const nextValue = options[newIndex].value;

          if (value === undefined) setUncontrolledValue(nextValue);
          onChange?.(nextValue);
          break;
        case 'ArrowUp':
          event.preventDefault();
          newIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
          const previousValue = options[newIndex].value;

          if (value === undefined) setUncontrolledValue(previousValue);
          onChange?.(previousValue);
          break;
        case 'Enter':
          event.preventDefault();
          setIsOpen(false);
          buttonRef.current?.focus();
          break;
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          buttonRef.current?.focus();
          break;
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, selectedValue, options, onChange, value]);

  return (
    <div ref={selectRef} className={cn('relative', className)}>
      {name ? <input type="hidden" name={name} value={selectedValue} /> : null}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        id={triggerId}
        data-act={dataAct}
        className={cn(
          'flex h-11 w-full items-center justify-between gap-2 rounded-control border border-line bg-surface px-3 text-sm text-ink lg:h-9',
          'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'transition-[border-color,background-color] duration-150 hover:border-line-strong',
          isOpen && 'border-accent ring-1 ring-accent'
        )}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${triggerId}-listbox`}
        aria-label={ariaLabel}
      >
        {selectedOption?.leading ? (
          <span aria-hidden="true" className="shrink-0 text-base leading-none">
            {selectedOption.leading}
          </span>
        ) : null}
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown
          className={cn('size-4 shrink-0 text-ink-2 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <div
          id={`${triggerId}-listbox`}
          className="absolute left-0 z-50 mt-1 w-full max-w-[calc(100vw-2rem)] rounded-card border border-line bg-surface p-1 shadow-[0_12px_32px_rgb(0_0_0/0.35)] [[data-theme=light]_&]:shadow-[0_12px_32px_rgb(0_0_0/0.12)]"
          role="listbox"
        >
          <div className="max-h-60 overflow-auto">
            {options.map((option) => {
              const isSelected = option.value === selectedValue;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    if (value === undefined) setUncontrolledValue(option.value);
                    onChange?.(option.value);
                    setIsOpen(false);
                    buttonRef.current?.focus();
                  }}
                  className={cn(
                    'min-h-11 w-full min-w-0 rounded-[6px] px-2.5 py-1.5 text-start text-sm transition-colors lg:min-h-8',
                    'hover:bg-surface-2 focus:bg-surface-2 focus:outline-none',
                    isSelected ? 'bg-accent-soft text-ink' : 'text-ink'
                  )}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {isSelected && <Check className="h-4 w-4 text-accent-text flex-shrink-0" />}
                    {option.leading ? (
                      <span aria-hidden="true" className="shrink-0 text-base leading-none">
                        {option.leading}
                      </span>
                    ) : null}
                    <span className="truncate">{option.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
